import ReactDOM from 'react-dom';
import React, { useState, useEffect, useRef } from 'react';
import { useRequestData, useLabels, useFlags, usePrefs } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import MyCarsModal from '../components/MyCarsModal';
import MyCarsContent from '../components/MyCarsContent';
import {
	MY_CARS_KEY,
	EVENT_TYPE,
	TOPIC_KEY,
	OPEN_MODAL_EVENT,
	MODAL_OPENED_EVENT,
	MODAL_CLOSED_EVENT,
	DISPLAY_MODE
} from '../global-constants';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useMyCarsContentActions } from '../hooks/useMyCarsContentActions';
import { getTrackingObjContainer } from '../utils/getTrackingObjContainer';

import {
	resetState,
	updateAlerts,
	unsubscribeAll,
	unsubscribeSingle,
	isStateEqual,
	updateLocalStorage
} from '../utils';
import '../global-sass/main.scss';
import { useFetchData, useFetchEAS, useIsChromeless } from '../hooks';
import DeferredComponent from '../ws-scripts/components/DeferredComponent';

const VehicleToast = React.lazy(() =>
	import(
		/* webpackChunkName: 'VehicleToast' */ '../components/vehicletoast/VehicleToast'
	)
);

function Widget() {
	const currentEventType = useRef();
	const isFirstModalVisibilityRender = useRef(true);
	const {
		moreRequestData: {
			accountId,
			siteId,
			parameterMap: { eventType = '', userId, priceAlertId, vehicleId }
		},
		myCarsData
	} = useSelector((state) => state);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [myCarsMobileIconRoot, setMyCarsMobileIconRoot] = useState(null);
	const { deviceType, windowId, widgetName } = useRequestData();
	const labels = useLabels();
	const flags = useFlags();
	const { enableSrpVehicleToast, displayMode } = usePrefs();
	const isInlineContent = displayMode === DISPLAY_MODE.INLINE_CONTENT;
	// Same definition useIsChromeless() derives internally -- called here too
	// (rather than deriving it by hand from displayMode) so there's exactly
	// one place that decides what counts as chromeless. MyCarsModal (the
	// isInlineContent ? ... : <MyCarsModal /> branch below) calls the same
	// hook itself now, resolving to the same thing this would have passed as
	// isChromeless={isHeadlessModal} -- it's only ever rendered when
	// !isInlineContent, so the hook can't disagree with that in practice.
	const isChromeless = useIsChromeless();
	const RECENTLY_VIEWED = labels.get('RECENTLY_VIEWED');
	const TRACKING_PRICE = labels.get('TRACKING_PRICE');
	const SAVED = labels.get('SAVED');
	const SHOP_OUR_INVENTORY = labels.get('SHOP_OUR_INVENTORY');
	const trackingObjLabels = {
		RECENTLY_VIEWED,
		TRACKING_PRICE,
		SAVED,
		SHOP_OUR_INVENTORY
	};
	const trackingObjContainer = getTrackingObjContainer(trackingObjLabels);
	const isDesktop = deviceType === 'DESKTOP';
	const { welcome: contentWelcome, onClickHandler: contentOnClickHandler } =
		useMyCarsContentActions(myCarsData);

	const isEnableMyCars =
		isBrowser &&
		window?.DDC?.siteProperties?.enableMyCars === 'true' &&
		!(window?.DDC?.siteProperties?.disableMyCarsToolbar === 'true');

	/* Insert new key into unset object here, this will also act as the template to backfill missing keys */
	useLocalStorage(
		MY_CARS_KEY,
		{
			email: '',
			accountId,
			newTrackedPriceCount: 0,
			newSaveCount: 0,
			vehicles: []
		},
		isEnableMyCars
	);

	const { response: alerts } = useFetchData({
		userId,
		eventType,
		isEnableMyCars
	});

	useFetchEAS({
		eventType,
		userId,
		priceAlertId,
		isEnableMyCars
	});

	useEffect(() => {
		if (isBrowser && !isDesktop) {
			const portalDiv = document.getElementById(
				'mycars-mobile-icon-root'
			);
			setMyCarsMobileIconRoot(portalDiv);
		}
	}, [isDesktop]);

	useEffect(() => {
		const openModal = () => setIsModalVisible(true);
		document.addEventListener(OPEN_MODAL_EVENT, openModal);
		return () => document.removeEventListener(OPEN_MODAL_EVENT, openModal);
	}, []);

	// Opening locks page scroll instantly, so MODAL_OPENED_EVENT fires the
	// moment isModalVisible becomes true — matching that timing.
	//
	// Closing works differently: react-bootstrap keeps scroll locked for
	// about 300ms after isModalVisible becomes false, until its close
	// animation actually finishes. Firing MODAL_CLOSED_EVENT immediately (the
	// same way we do for opening) caused a real bug: the drawer removed its
	// scrollbar-width padding before the real scrollbar had reappeared,
	// causing a visible jump. So MODAL_CLOSED_EVENT fires from
	// handleModalExited instead, which only runs once the close animation is
	// actually done.
	useEffect(() => {
		if (isFirstModalVisibilityRender.current) {
			isFirstModalVisibilityRender.current = false;
			return;
		}
		if (isModalVisible) {
			document.dispatchEvent(new CustomEvent(MODAL_OPENED_EVENT));
		}
	}, [isModalVisible]);

	const handleModalExited = () => {
		document.dispatchEvent(new CustomEvent(MODAL_CLOSED_EVENT));
	};

	useEffect(() => {
		if (
			alerts &&
			alerts.length > 0 &&
			myCarsData?.vehicles &&
			userId &&
			isEnableMyCars
		) {
			let newState = updateAlerts(myCarsData, alerts);
			if (
				(eventType === EVENT_TYPE.VALIDATE || !eventType) &&
				!isStateEqual(myCarsData, newState)
			) {
				updateLocalStorage(newState, MY_CARS_KEY, TOPIC_KEY);
			}
			if (eventType === EVENT_TYPE.UNSUBSCRIBE_SINGLE) {
				newState = unsubscribeSingle(newState, vehicleId);
				updateLocalStorage(newState, MY_CARS_KEY, TOPIC_KEY);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alerts]);

	useEffect(() => {
		if (
			myCarsData &&
			myCarsData?.vehicles &&
			eventType &&
			eventType === EVENT_TYPE.UNSUBSCRIBE_ALL &&
			currentEventType.current !== eventType &&
			isEnableMyCars
		) {
			currentEventType.current = eventType;
			const newState = unsubscribeAll(myCarsData);
			updateLocalStorage(newState, MY_CARS_KEY, TOPIC_KEY);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [myCarsData]);

	const onModalClose = () => {
		const trackingObj = trackingObjContainer['MyCars nav close'];
		trackEvent(widgetName, windowId, trackingObj);
		setIsModalVisible(false);
		const newState = resetState(myCarsData);
		if (!isStateEqual(myCarsData, newState)) {
			updateLocalStorage(newState, MY_CARS_KEY, TOPIC_KEY);
		}
	};

	const showBadge =
		myCarsData &&
		(myCarsData.newSaveCount > 0 || myCarsData.newTrackedPriceCount > 0);

	const iconClick = () => {
		const trackingObj = trackingObjContainer['MyCars nav'];
		trackEvent(widgetName, windowId, trackingObj);
		setIsModalVisible(true);
	};

	const wrapperClasses = [
		'ws-mycars-nav-wrapper',
		isDesktop ? 'd-flex align-items-center justify-content-center' : ''
	];
	const buttonWrapperClasses = [
		isDesktop ? 'd-flex mycars-integration-navbar' : ''
	];
	const buttonClasses = [
		'btn-link btn-no-decoration border-0 text-decoration-none position-relative',
		isDesktop ? 'pl-4' : '',
		isModalVisible ? 'active' : ''
	];

	const widgetContent = (
		<>
			<div className={setClassNames(wrapperClasses)}>
				{isDesktop && <div data-location="header-toolbar" />}

				{isEnableMyCars && !isChromeless && (
					<div className={setClassNames(buttonWrapperClasses)}>
						<button
							type="button"
							className={setClassNames(buttonClasses)}
							aria-label={labels.get('OPEN_MY_CARS')}
							aria-pressed={isModalVisible}
							onClick={iconClick}
						>
							<i className="ddc-icon ddc-nav-icon ddc-icon-mycars-minimal-login ddc-icon-size-xlarge" />
							{showBadge && (
								<span
									className={
										isDesktop
											? 'mycars-nav-badge--desktop'
											: 'mycars-nav-badge--mobile'
									}
									data-testid="mycars-nav-badge"
								/>
							)}
						</button>
					</div>
				)}
			</div>
			{myCarsData &&
				myCarsData?.vehicles &&
				isEnableMyCars &&
				(isInlineContent ? (
					<MyCarsContent
						isMobile={!isDesktop}
						myCarsData={myCarsData}
						welcome={contentWelcome}
						onClickHandler={contentOnClickHandler}
					/>
				) : (
					<MyCarsModal
						isOpen={isModalVisible}
						onModalClose={onModalClose}
						onExited={handleModalExited}
					/>
				))}
			{flags['enable-vehicle-toast'] &&
				enableSrpVehicleToast === 'true' && (
					<DeferredComponent>
						<VehicleToast siteId={siteId} />
					</DeferredComponent>
				)}
		</>
	);

	if (!isEnableMyCars && !isDesktop) {
		return null;
	}

	if (isDesktop || isInlineContent) {
		return widgetContent;
	}

	if (myCarsMobileIconRoot) {
		return ReactDOM.createPortal(widgetContent, myCarsMobileIconRoot);
	}

	return null;
}

export default Widget;
